import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { SectionHead, Badge, StatTile } from './academyShared'

// The course's fixed installment plan, plus what its students still owe.
//
// Over GET/PUT /courses/:id/installments and GET /courses/:id/outstanding,
// which sit behind the same author + ownership gates as the rest of the
// instructor console — so this only ever shows a course the signed-in
// instructor actually teaches.
//
// The whole plan saves in one PUT because the rows are only valid as a set:
// their amounts have to add up to the course price, and the API rejects a plan
// that doesn't. The running total below is shown live so that lands as a
// reconciliation the instructor can see rather than a 422 after the fact.

const goldBtnCls =
  'bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed'
const outlineBtnCls =
  'border border-stone-200 text-stone-700 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-stone-50 transition-all disabled:opacity-60 disabled:cursor-not-allowed'
const inputCls =
  'w-full bg-stone-50 border rounded-xl px-3 py-2.5 text-sm text-astroDark placeholder:text-stone-300 focus:outline-none focus:ring-2 focus:ring-goldPrimary/30 border-stone-200'

const rupees = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

/** "Due at checkout" reads better than "due in 0 days" for the opening payment. */
function dueLabel(days) {
  const n = Number(days) || 0
  if (n <= 0) return 'at checkout'
  if (n % 30 === 0) return `${n / 30} month${n === 30 ? '' : 's'} after enrolling`
  return `${n} days after enrolling`
}

export default function CourseInstallmentPlan({ courseId, price, acceptPartPayment, minPartPrice }) {
  const queryClient = useQueryClient()
  const [rows, setRows] = useState(null)
  const [banner, setBanner] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'course-installments', String(courseId)],
    queryFn: () => api.courses.installments(courseId),
    enabled: Boolean(courseId),
  })
  const { data: owing } = useQuery({
    queryKey: ['dashboard', 'course-outstanding', String(courseId)],
    queryFn: () => api.courses.outstanding(courseId),
    enabled: Boolean(courseId),
  })

  // Seed the editor once from the server, then leave it alone — re-syncing on
  // every refetch would throw away whatever is mid-way through being typed.
  useEffect(() => {
    const server = data?.data?.installments
    if (server && rows === null) {
      setRows(
        server.map((row) => ({
          label: row.label ?? '',
          amount: String(Number(row.amount)),
          due_days: String(row.due_days ?? 0),
        })),
      )
    }
  }, [data, rows])

  const saveMutation = useMutation({
    mutationFn: (next) =>
      api.courses.saveInstallments(courseId, {
        installments: next.map((row) => ({
          label: row.label || null,
          amount: Number(row.amount),
          due_days: Number(row.due_days || 0),
        })),
      }),
    onSuccess: () => {
      setBanner(null)
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-installments', String(courseId)] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail', String(courseId)] })
    },
    onError: (err) => {
      // The API's 422s land under `details`, keyed "installments" or
      // "installments.0.amount" — surfaced as one banner rather than inline,
      // since the index keys don't map onto a react-hook-form field here.
      const details = err?.details
      const first = details && typeof details === 'object' ? Object.values(details)[0] : null
      setBanner((Array.isArray(first) ? first[0] : first) || err?.message || 'Could not save the plan.')
    },
  })

  const current = rows ?? []
  // The stored price, not an unsaved form value — the API checks against what
  // is in the database, so reconciling against anything else would mislead.
  const coursePrice = Number(data?.data?.price ?? price) || 0
  const minimum = Number(data?.data?.min_part_price ?? minPartPrice) || 0
  const total = current.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)
  const difference = Math.round((coursePrice - total) * 100) / 100

  const update = (index, patch) =>
    setRows(current.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const removeRow = (index) => setRows(current.filter((_, i) => i !== index))

  // A two-part deposit/balance split is the common case, so starting a plan
  // opens with both halves filled rather than one blank row the API rejects
  // (a one-installment plan is just the full price).
  const addRow = () => {
    if (!current.length) {
      const half = Math.round((coursePrice / 2) * 100) / 100
      setRows([
        { label: 'Deposit', amount: coursePrice ? String(half) : '', due_days: '0' },
        { label: 'Balance', amount: coursePrice ? String(Math.round((coursePrice - half) * 100) / 100) : '', due_days: '30' },
      ])
      return
    }
    const last = current[current.length - 1]
    setRows([...current, { label: '', amount: '', due_days: String(Number(last.due_days || 0) + 30) }])
  }

  const canSave = current.length === 0 || (difference === 0 && current.every((row) => Number(row.amount) > 0))
  const report = owing?.data

  if (!acceptPartPayment) {
    return (
      <div className="dash-card p-4 sm:p-6">
        <SectionHead eyebrow="Vedic Academy · Pricing" eyebrowClass="text-amber-700" title="Installment Plan" />
        <p className="text-sm text-stone-500">
          Part payment is off for this course, so the full {rupees(coursePrice)} is due at checkout. Turn on
          &ldquo;Accept part payment&rdquo; in <strong>Edit Course</strong> to let students pay in stages.
        </p>
      </div>
    )
  }

  return (
    <div className="dash-card p-4 sm:p-6">
      <SectionHead
        eyebrow="Vedic Academy · Pricing"
        eyebrowClass="text-amber-700"
        title="Installment Plan"
        count={current.length || undefined}
      >
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={addRow} disabled={rows === null} className={outlineBtnCls}>
            ＋ {current.length ? 'Add Installment' : 'Create Plan'}
          </button>
          {rows !== null && (
            <button
              type="button"
              onClick={() => { setBanner(null); saveMutation.mutate(current) }}
              disabled={saveMutation.isPending || !canSave}
              title={canSave ? undefined : 'The installments must add up to the course price first'}
              className={goldBtnCls}
            >
              {saveMutation.isPending ? 'Saving…' : current.length ? 'Save Plan' : 'Clear Plan'}
            </button>
          )}
        </div>
      </SectionHead>

      {banner && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{banner}</div>
      )}

      <p className="text-xs text-stone-400 mb-4">
        Students pay these in order. Due dates count from the day each student enrolls, so the schedule works
        whenever they buy. Leave the plan empty to let them pay any amount from {rupees(minimum)} upwards instead.
      </p>

      {isLoading && rows === null ? (
        <p className="text-sm text-stone-400">Loading plan…</p>
      ) : current.length === 0 ? (
        <p className="text-sm text-stone-500">
          No fixed plan — part payment is open-balance: a student pays at least {rupees(minimum)} up front and
          clears the rest whenever they like.
        </p>
      ) : (
        <div className="space-y-3">
          {current.map((row, index) => (
            <div key={index} className="flex flex-col sm:flex-row sm:items-center gap-2.5">
              <span className="num w-7 h-7 shrink-0 rounded-full bg-[#F5E6D3] text-[#A67C00] text-xs font-bold flex items-center justify-center">
                {index + 1}
              </span>
              <input
                value={row.label}
                onChange={(e) => update(index, { label: e.target.value })}
                placeholder={index === 0 ? 'Deposit' : `Installment ${index + 1}`}
                className={`${inputCls} sm:max-w-44`}
              />
              <input
                type="number"
                min="0"
                step="0.01"
                value={row.amount}
                onChange={(e) => update(index, { amount: e.target.value })}
                placeholder="Amount ₹"
                className={`${inputCls} sm:max-w-32`}
              />
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={row.due_days}
                  onChange={(e) => update(index, { due_days: e.target.value })}
                  className={`${inputCls} sm:max-w-24`}
                />
                <span className="text-xs text-stone-400 truncate">Due {dueLabel(row.due_days)}</span>
              </div>
              <button
                type="button"
                onClick={() => removeRow(index)}
                aria-label={`Remove installment ${index + 1}`}
                className="w-9 h-9 shrink-0 rounded-xl border border-stone-200 text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors self-start sm:self-auto"
              >
                🗑
              </button>
            </div>
          ))}

          <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-stone-100">
            <span className="text-xs text-stone-500">
              Scheduled <strong className="text-astroDark">{rupees(total)}</strong> of {rupees(coursePrice)}
            </span>
            {difference === 0 ? (
              <Badge className="bg-green-50 text-green-600">Adds up</Badge>
            ) : difference > 0 ? (
              <Badge className="bg-amber-50 text-amber-700">{rupees(difference)} unscheduled</Badge>
            ) : (
              <Badge className="bg-red-50 text-red-500">{rupees(-difference)} over the price</Badge>
            )}
          </div>
        </div>
      )}

      {/* What the plan is actually collecting. A student an admin enrolled by
          hand and never charged shows here as owing the full price, which is
          the case a payments-only report would miss entirely. */}
      {report && (
        <div className="mt-5 pt-5 border-t border-stone-100">
          <div className="flex flex-wrap gap-3">
            <StatTile icon="💰" label="Collected" value={rupees(report.total_collected)} />
            <StatTile icon="⏳" label="Outstanding" value={rupees(report.total_outstanding)} />
            <StatTile icon="🎓" label="Enrolled" value={report.total_enrollments} />
          </div>

          {report.items?.some((row) => row.balance > 0) && (
            <div className="mt-4 space-y-2">
              <p className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Students with a balance</p>
              {report.items
                .filter((row) => row.balance > 0)
                .map((row) => (
                  <div
                    key={row.enrollment_id}
                    className="flex flex-wrap items-center justify-between gap-2 bg-stone-50 border border-stone-100 rounded-xl px-4 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-astroDark truncate">
                        {[row.user?.first_name, row.user?.last_name].filter(Boolean).join(' ') || 'Student'}
                      </p>
                      <p className="text-[10px] text-stone-400 truncate">
                        {row.user?.email || row.user?.mobile_no || '—'}
                        {row.next_installment
                          ? ` · next: ${row.next_installment.label || `Installment ${row.next_installment.position}`}`
                          : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-stone-400">{rupees(row.paid)} paid</span>
                      <span className="text-sm font-bold text-red-500">{rupees(row.balance)} due</span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
